import { useMemo } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { faList } from '@fortawesome/pro-regular-svg-icons';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { capitalize, identity } from 'lodash';
import { ReportResource } from 'types/domain';

interface ReportCategoryFilterFormValues extends FilterFormValue<ReportResource[]> {}

export const filterKey: ServerKey = 'resource';
export const Label: LabelFilterComponent = () => 'Type';

export const Icon: FilterIcon = faList;

export const Filter: ContentFilterComponent = () => {
    const { defaultValue, ...rest } = useFilter<ReportResource[]>();
    const methods = Form.useForm<ReportCategoryFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });
    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>Type</Form.Label>
                            <Form.Combobox
                                name="value"
                                control={control}
                                rules={{
                                    required: ValidationTextMap.Required,
                                }}
                            >
                                <FilterComboboxItems>
                                    <Combobox.Item value="contacts">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Contacts</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="transactions">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>Transactions</Combobox.ItemText>
                                    </Combobox.Item>
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<ReportResource[]>();
    const value = currentFilter?.value ?? [];

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: capitalize,
            })}
        </ReadableFilterValue>
    );
};

export const downstream: DownstreamFilterTransform<ReportResource> = identity;

export const upstream: UpstreamFilterTransform<ReportResource> = (filter: FilterValue) =>
    injectEquals({
        value: filter.value,
    });
